import type { ContextSnapshot } from './assistantContext';

/**
 * Static registry of RxAssistant quick-action chips. Each entry binds
 * to one `contextKey` (matching a tab's `useAssistantContext` key);
 * the chip rack filters this list by the currently-active context.
 *
 * `buildPrompt` returns the full message sent to the model. The chip's
 * `label` is what the clinician sees in the chat bubble; the prompt
 * payload (which embeds the JSON context inside `<context>` tags so
 * the model can pick it out cleanly) is hidden behind a preview pill.
 *
 * Keep labels short — the chip rack uses a compact pill style and a
 * long label both stretches the chip and wraps awkwardly.
 */

export type QuickAction = {
  id: string;
  /** Which context registration(s) this applies to. A chip can target
   *  several context keys (e.g. both `logs-v2` and `logs-v3`). */
  contextKey: string | string[];
  /** Chip text + user bubble text. Keep ≤ 28 chars where possible. */
  label: string;
  /** Build the full prompt sent to the model. Context is already
   *  fetched + serialized — caller passes it in. */
  buildPrompt: (ctx: ContextSnapshot) => string;
};

/**
 * Standing guidance prepended to every quick-action prompt. Covers
 * cases the chip-specific prompt shouldn't have to keep re-stating —
 * notably that RDI bands / clinician goals in the payload can be
 * incomplete or internally inconsistent, and the model should name
 * that ambiguity rather than paper over it. Goes BEFORE the chip's
 * own intro so the chip's task framing still reads as the headline.
 */
const CONTEXT_GUIDANCE = [
  'The <context> block below is a structured snapshot of what the clinician is looking at.',
  'Treat the data as authoritative for VALUES (averages, counts), but be skeptical of TARGETS:',
  'RDI ranges may be wide, partial (only `rdi_low` or only `rdi_high`), or null, and',
  'patient-specific goals can overlap or contradict each other. When that happens, briefly',
  'flag the ambiguity and let the clinician resolve it — do NOT invent a single "correct" target.',
  'Write replies in natural clinical language: never surface raw field names, keys, or variable',
  'names from the context (e.g. say "protein target", not "protein_g") — translate them to plain words.',
].join(' ');

/**
 * Compose the user-facing message text into a model-prompt that
 * embeds the context snapshot. Keeps the JSON inside `<context>` tags
 * so the model can address it directly ("based on the data in
 * <context>...") and we have a stable marker for the preview-pill
 * extractor.
 */
const wrapWithContext = (intro: string, ctx: ContextSnapshot): string =>
  `${intro}\n\n${CONTEXT_GUIDANCE}\n\n<context source="${ctx.title}">\n${JSON.stringify(ctx.data, null, 2)}\n</context>`;

export const QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'nutrients.summarize',
    contextKey: 'nutrients',
    label: 'Summarize nutrients',
    buildPrompt: (ctx) =>
      wrapWithContext(
        `Summarize this patient's nutrient profile for the visible date range. ` +
          `Call out anything materially over or under the RDI, and any diet-quality patterns worth raising with the patient. ` +
          `If a nutrient has an attached clinician goal (\`goals\` field), prefer that as the comparison anchor over the RDI band. ` +
          `Any nutrient marked \`goals_conflict: true\` has competing prescriptions — flag it briefly and don't pick a side. ` +
          `Stay concise — 4-6 bullets, no preamble.`,
        ctx,
      ),
  },
  {
    id: 'emotions.summarize',
    contextKey: 'emotions',
    label: 'Summarize wellbeing',
    buildPrompt: (ctx) =>
      wrapWithContext(
        `Summarize this patient's ${'topic' in (ctx.data as object) ? (ctx.data as { topic: string }).topic.toLowerCase() : 'wellbeing'} ` +
          `entries for the visible date range. The context carries the actual question text, the option set the patient chose from, ` +
          `aggregate counts (\`by_option\`), and per-day counts (\`by_day\`). ` +
          `Focus on: dominant answer(s), trend over the range (improving / stable / declining), unusually high/low days, ` +
          `and gaps where the patient logged nothing. Don't fabricate clinical interpretations — just describe the pattern. ` +
          `Stay concise — 4-6 bullets, no preamble.`,
        ctx,
      ),
  },
  {
    id: 'emotions.summarize_mindful',
    contextKey: 'emotions',
    label: 'Summarize reflections',
    buildPrompt: (ctx) =>
      wrapWithContext(
        `Summarize this patient's mindful-eating reflections in the visible date range. ` +
          `The context's \`mindful_eating.entries\` array (newest first, capped at 20) carries the meal each reflection ` +
          `was about (date, time, meal type, food preview) and the patient's actual Q/A answers. ` +
          `Focus on: recurring themes across reflections (e.g. eating speed, hunger/fullness cues, environment, emotional drivers), ` +
          `which meals/times of day generate the most reflection, and any notable shifts over the range. ` +
          `If the array is missing or empty, say so plainly — don't fabricate. ` +
          `Stay concise — 4-6 bullets, no preamble.`,
        ctx,
      ),
  },
  {
    id: 'emotions.patient_message',
    contextKey: 'emotions',
    label: 'Draft patient message',
    buildPrompt: (ctx) =>
      wrapWithContext(
        `Draft a brief, supportive message (2-3 sentences) I could send this patient, grounded in ` +
          `their wellbeing entries in the snapshot. Warm and specific — reference what they actually logged — ` +
          `not clinical or preachy. If there's little to go on, keep it general and encouraging. ` +
          `Output ONLY the message text, no preamble.`,
        ctx,
      ),
  },

  // ── Summary tab ──
  {
    id: 'summary.overview',
    contextKey: 'summary',
    label: 'Summarize patient',
    buildPrompt: (ctx) =>
      wrapWithContext(
        `Give a concise clinical overview of this patient for the visible date range using the snapshot ` +
          `(logging engagement, calories/day, time-in-range, meal-timing pattern, diet-quality score). ` +
          `Lead with what stands out. 4-6 bullets, no preamble; don't restate every number.`,
        ctx,
      ),
  },
  {
    id: 'summary.focus',
    contextKey: 'summary',
    label: 'What to focus on',
    buildPrompt: (ctx) =>
      wrapWithContext(
        `From the snapshot, identify the 2-3 highest-priority things I should focus on with this patient ` +
          `next. Tie each to specific data and suggest a concrete next step. Be direct; if the data is too ` +
          `thin to prioritize, say what's missing.`,
        ctx,
      ),
  },

  // ── Food log (Logs v2 + v3) ──
  {
    id: 'foodlog.summarize',
    contextKey: ['logs-v2', 'logs-v3'],
    label: 'Summarize this range',
    buildPrompt: (ctx) =>
      wrapWithContext(
        `Summarize this patient's eating across the days in the snapshot — typical meal pattern and timing, ` +
          `macro balance, and any days with missing or sparse logging. 4-6 bullets, no preamble.`,
        ctx,
      ),
  },
  {
    id: 'foodlog.concerns',
    contextKey: ['logs-v2', 'logs-v3'],
    label: 'Flag nutrition concerns',
    buildPrompt: (ctx) =>
      wrapWithContext(
        `Review the per-day meals and macros and flag nutrition concerns worth raising — e.g. skipped meals, ` +
          `low protein, refined-carb-heavy or oversized evening meals, low fiber, long gaps between meals. ` +
          `Cite the specific dates. Don't fabricate concerns the data doesn't support; if it looks fine, say so.`,
        ctx,
      ),
  },
  {
    id: 'foodlog.glucose',
    contextKey: ['logs-v2', 'logs-v3'],
    label: 'Post-meal glucose',
    buildPrompt: (ctx) =>
      wrapWithContext(
        `Using the per-day \`glucose\` fields (average + time-in-range) alongside the meals, comment on ` +
          `post-meal glucose patterns — which days/meals coincided with highs or low time-in-range. ` +
          `If a day has no glucose data, skip it; if there's no CGM data at all, say so plainly.`,
        ctx,
      ),
  },

  // ── Goals tab ──
  {
    id: 'goals.review',
    contextKey: 'prescriptions',
    label: 'Review goal progress',
    buildPrompt: (ctx) =>
      wrapWithContext(
        `Summarize this patient's active goals and how they're tracking over the visible range using the ` +
          `snapshot. Note which look on-track vs lagging, and any that overlap or conflict. 3-5 bullets. ` +
          `If range-level adherence data is absent, work from the goal definitions and say adherence is unavailable.`,
        ctx,
      ),
  },
  {
    id: 'goals.suggest',
    contextKey: 'prescriptions',
    label: 'Suggest next goal',
    buildPrompt: (ctx) =>
      wrapWithContext(
        `Based on the current goals in the snapshot, suggest one or two realistic next goals for this patient. ` +
          `Make them specific and measurable, avoid duplicating existing goals, and give a one-line rationale ` +
          `for each.`,
        ctx,
      ),
  },

  // ── Nutrients tab (extra) ──
  {
    id: 'nutrients.patient_note',
    contextKey: 'nutrients',
    label: 'Draft patient note',
    buildPrompt: (ctx) =>
      wrapWithContext(
        `Draft a short, encouraging patient-facing note (3-4 sentences) summarizing their nutrient highlights ` +
          `and one or two gentle, specific suggestions, based on the snapshot. Plain language, no jargon, no ` +
          `RDI numbers unless they help. Output ONLY the note text.`,
        ctx,
      ),
  },

  // ── Default chips — appear on every context-bearing tab. Generic starting
  //    points that embed whatever the page's hidden snapshot is, so the
  //    assistant is useful before the clinician types anything.
  {
    id: 'default.summarize',
    contextKey: '*',
    label: 'Summarize this view',
    buildPrompt: (ctx) =>
      wrapWithContext(
        `Summarize what I'm currently looking at for this patient, using the snapshot. ` +
          `Lead with what stands out; a few bullets, no preamble.`,
        ctx,
      ),
  },
  {
    id: 'default.concerns',
    contextKey: '*',
    label: 'Anything concerning?',
    buildPrompt: (ctx) =>
      wrapWithContext(
        `Based on the snapshot, is there anything concerning or worth my attention here? ` +
          `Be specific and cite the data; if nothing stands out, say so plainly.`,
        ctx,
      ),
  },
  {
    id: 'default.explain',
    contextKey: '*',
    label: 'Explain this data',
    buildPrompt: (ctx) =>
      wrapWithContext(
        `Briefly explain what this data shows and how to interpret it as a clinician — ` +
          `short and practical, no more than a few sentences.`,
        ctx,
      ),
  },
];

/**
 * Read the visible chips for the current context: the context-specific chips,
 * plus any default chips (contextKey `'*'`) that appear on every tab which
 * registered a snapshot.
 */
export const quickActionsFor = (contextKey: string): QuickAction[] =>
  QUICK_ACTIONS.filter((a) => {
    const keys = Array.isArray(a.contextKey) ? a.contextKey : [a.contextKey];
    return keys.includes('*') || keys.includes(contextKey);
  });
